import { Client } from "domain/entities/client";
import { ClientInputFilter } from "domain/models/client-input-filter";
import Spinner from "presentation/components/Shared/Spinner";
import { useClientByFilter } from "presentation/hooks/use-query-client";
import { useClientStore } from "presentation/stores/use-client-store";
import { useFilterClientStore } from "presentation/stores/use-filter-client-store";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import HeaderClientGrid from "./HeaderClientGrid";

const ClientGrid: React.FC = () => {
  const navigate = useNavigate();
  const { filters, setFilters } = useFilterClientStore();
  const { client, setClient } = useClientStore();
  const toast = useRef<Toast>(null);
  const [lazyParams, setLazyParams] = useState({
    sortBy: "id",
    sortOrder: "desc",
    limit: 50,
    page: 1,
    filters: {},
  });

  const {
    data: clients,
    isLoading,
    isError,
    error,
    refetch,
  } = useClientByFilter({
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
  } as ClientInputFilter);

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if ("constraints" in value) {
              return [key, value.constraints?.[0]?.value ?? ""];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
          )
      ),
    }));
  }, [filters]);

  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail: error?.message || "Ocorreu um erro inesperado",
      life: 1500,
    });
  }

  const showActionOptions = (rowData: Client) => {
    return (
      <div>
        <i
          className="pi pi-pencil cursor-pointer"
          onClick={() => navigate(`/clients/edit/${rowData.id}`)}
        />
      </div>
    );
  };

  return (
    <div>
      <DataTable
        value={clients?.data || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={clients?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={<HeaderClientGrid />}
        rowsPerPageOptions={[2, 10, 25, 50]}
        paginatorLeft
        dataKey="id"
        globalFilterFields={["name", "city", "type"]}
      >
        <Column
          field="name"
          header="Nome"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="type"
          header="Tipo"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="city"
          header="Cidade"
          sortable
          filter
          showFilterMatchModes={false}
        />

        <Column
          body={showActionOptions}
          header="Ações"
          align="center"
          alignHeader="center"
        />
      </DataTable>
      <Toast ref={toast} position="bottom-right" />
    </div>
  );
};

export default ClientGrid;
